import { Loader2, Package } from 'lucide-react';
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { ui } from '../../styles/ui';

const CATEGORY_OPTIONS = ['Medicine', 'Food', 'Other'];

export const CreateCompanyPage = () => {
  const navigate = useNavigate();
  const { createCompany, authToken } = useAppState();
  const [formData, setFormData] = useState({ name: '', category: '', gstNo: '', area: '', city: '', state: '', pincode: '' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // This step only makes sense right after registering (need a token but no
  // company yet). Anyone landing here without a token should just log in.
  if (!authToken) {
    return <Navigate to="/login" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError('');

    try {
      if (!formData.name || !formData.category || !formData.gstNo || !formData.area || !formData.city || !formData.state || !formData.pincode) {
        throw new Error('Please fill in all required fields');
      }

      const companyData = {
        name: formData.name,
        category: formData.category,
        gstNo: formData.gstNo,
        address: { area: formData.area, city: formData.city, state: formData.state, pincode: formData.pincode },
      };

      await createCompany(companyData);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(err.message || 'Failed to create company');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className={`${ui.card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-violet-600">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Create Company</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Setup your company profile</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={ui.label}>
                Company Name <span className="text-red-500">*</span>
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={ui.input} placeholder="Enter company name" required disabled={submitLoading} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={ui.label}>
                  Category <span className="text-red-500">*</span>
                </label>
                <select name="category" value={formData.category} onChange={handleInputChange} className={ui.input} required disabled={submitLoading}>
                  <option value="" disabled>Select a category</option>
                  {CATEGORY_OPTIONS.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={ui.label}>
                  GST Number <span className="text-red-500">*</span>
                </label>
                <input type="text" name="gstNo" value={formData.gstNo} onChange={handleInputChange} className={ui.input} placeholder="Enter GST number" required disabled={submitLoading} />
              </div>
            </div>

            <div>
              <label className={ui.label}>
                Area <span className="text-red-500">*</span>
              </label>
              <input type="text" name="area" value={formData.area} onChange={handleInputChange} className={ui.input} placeholder="Enter area/locality" required disabled={submitLoading} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={ui.label}>
                  City <span className="text-red-500">*</span>
                </label>
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} className={ui.input} placeholder="Enter city" required disabled={submitLoading} />
              </div>
              <div>
                <label className={ui.label}>
                  State <span className="text-red-500">*</span>
                </label>
                <input type="text" name="state" value={formData.state} onChange={handleInputChange} className={ui.input} placeholder="Enter state" required disabled={submitLoading} />
              </div>
            </div>

            <div>
              <label className={ui.label}>
                Pincode <span className="text-red-500">*</span>
              </label>
              <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} className={ui.input} placeholder="Enter pincode" required disabled={submitLoading} />
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
                {submitError}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button type="submit" disabled={submitLoading} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2">
                {submitLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating Company...
                  </>
                ) : (
                  <>
                    <Package className="h-4 w-4" />
                    Create Company
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
